/**
 * Map of breakpoints to random number ranges as factor for the mousemove delta
 */
const factorBreakpointMap = {
  none: [0, 0],
  xs: [0, 0],
  s: [0.05, 0.1],
  m: [0.05, 0.2],
  l: [0.05, 0.3]
};

/**
 * Map of breakpoints to random number ranges as factor for the parallax scroll delta
 */
const scrollFactorBreakpointMap = {
  none: [0, 0],
  xs: [0, 0],
  s: [0.01, 0.1],
  m: [0.01, 0.1],
  l: [0.01, 0.1]
};

/**
 * Map of breakpoints to a bool whether mouse movement is enabled
 */
const enableMouseMoveBreakpointMap = {
  none: false,
  xs: false,
  s: true,
  m: true,
  l: true
};

/**
 * Get a random number on a range
 *
 * @param {number} min Lower bound
 * @param {number} max Upper bound
 * @returns {number} Number between min and max
 */
const getRandomNumber = (min, max) => Math.random() * (max - min) + min;

const Fragments = () => ({
  /**
   * X axis distance to the center of all fragments, in pixels
   * @type {number}
   */
  distanceToCenterX: 0,

  /**
   * Y axis distance to the center of all fragments, in pixels
   * @type {number}
   */
  distanceToCenterY: 0,

  /**
   * Factor for the X distance, between 0 and 1
   * @type {number}
   */
  factorX: 0,

  /**
   * Factor for the Y distance transform, between 0 and 1
   * @type {number}
   */
  factorY: 0,

  /**
   * Factor for the scroll delta of the parallax transform, between 0 and 1
   * @type {number}
   */
  scrollFactorY: 0,

  /**
   * X transform towards the center, in pixels
   * @type {number}
   */
  translateX: 0,

  /**
   * Y transform towards the center, in pixels
   * @type {number}
   */
  translateY: 0,

  /**
   * X axis mouse move direction, either 1 or -1
   * @type {number}
   */
  moveDirectionX: 1,

  /**
   * Y axis mouse move direction, either 1 or -1
   * @type {number}
   */
  moveDirectionY: 1,

  /**
   * The current breakpoint. One of none, xs, s, m, l
   * @see {src/tokens/_breakpoints.scss}
   * @type {string}
   */
  breakpoint: 'none',

  /**
   * Whether mouse movement is enabled
   * @type {boolean}
   */
  enableMouseMove: false,

  get style() {
    return {
      transform: `translate(${this.translateX}px, ${this.translateY}px)`
    };
  },

  /**
   * Move fragment a bit to the center of all fragments
   * @returns {void}
   */
  updateDistances() {
    /**
     * Bounding box of the fragments container
     * @type {DOMRect}
     */
    const parentBB = this.$root
      .closest('.fragments__fragments')
      .getBoundingClientRect();

    /**
     * Bounding box of this fragment
     * @type {DOMRect}
     */
    const thisBB = this.$root.getBoundingClientRect();

    const parentCenterX = parentBB.left + parentBB.width / 2;
    const thisCenterX = thisBB.left + thisBB.width / 2;

    this.distanceToCenterX = parentCenterX - thisCenterX;

    const parentCenterY = parentBB.top + parentBB.height / 2;
    const thisCenterY = thisBB.top + thisBB.height / 2;

    this.distanceToCenterY = parentCenterY - thisCenterY;

    this.translateX = this.factorX * this.distanceToCenterX;
    this.translateY = this.factorY * this.distanceToCenterY;
  },

  /**
   * Global mouse move event handler
   * @param {MouseEvent} ev Event handler
   * @returns {void}
   */
  onMouseMove(ev) {
    this.translateX += this.moveDirectionX * 0.025 * ev.movementX;
    this.translateY += this.moveDirectionY * 0.025 * ev.movementY;
  },

  /**
   * Pointer change event handler
   * @see {https://developer.mozilla.org/en-US/docs/Web/CSS/@media/pointer}
   * @param {boolean} isFine Whether the primary input mechanism is an accurate pointing device, e.g. a mouse
   * @returns {void}
   */
  onPointerChange(isFine) {
    this.enableMouseMove = isFine;
  },

  /**
   * Window resize event handler
   * @returns {void}
   */
  onResize() {
    const breakpoint = getComputedStyle(
      document.documentElement
    ).getPropertyValue('--breakpoint');

    if (breakpoint !== this.breakpoint) {
      this.breakpoint = breakpoint;
    }

    this.updateDistances();
  },

  init() {
    this.onMouseMove = this.onMouseMove.bind(this);

    this.$watch('enableMouseMove', (isEnabled) => {
      if (isEnabled) {
        window.addEventListener('mousemove', this.onMouseMove);
      } else {
        window.removeEventListener('mousemove', this.onMouseMove);
        this.updateDistances();
      }
    });

    this.$watch('breakpoint', (breakpoint) => {
      /* Generate random factors for moving the fragment towards the
       * center point between all fragments. The factor is applied to the
       * distance between the fragment's center and the center point.
       */
      this.factorX = getRandomNumber.apply(
        null,
        factorBreakpointMap[breakpoint]
      );
      this.factorY = getRandomNumber.apply(
        null,
        factorBreakpointMap[breakpoint]
      );

      /* Generate a random factor for the parallax effect */
      this.scrollFactorY = getRandomNumber.apply(
        null,
        scrollFactorBreakpointMap[breakpoint]
      );

      this.enableMouseMove = enableMouseMoveBreakpointMap[breakpoint];

      this.moveDirectionX = Math.round(Math.random()) ? 1 : -1;
      this.moveDirectionY = Math.round(Math.random()) ? 1 : -1;

      this.updateDistances();
    });

    window.addEventListener('resize', () => this.onResize());
    this.onResize();

    const mq = window.matchMedia('(pointer: fine)');
    mq.addEventListener('change', ({ matches }) =>
      this.onPointerChange(matches)
    );
    this.onPointerChange(mq.matches);

    let isVisible = false;

    const obs = new IntersectionObserver((entries) => {
      isVisible = entries[0].isIntersecting;
    });
    obs.observe(this.$root.closest('.fragments__fragments'));

    let lastScrollTop = window.scrollY;

    window.addEventListener('scroll', () => {
      const scrollTop = window.scrollY;
      const delta = scrollTop - lastScrollTop;
      lastScrollTop = scrollTop;

      if (isVisible) {
        this.translateY -= this.scrollFactorY * delta;
      }
    });
  }
});

export default Fragments;
